import React, { useEffect, useRef } from 'react';
import * as THREE from 'three';

function QuantumBackground() {
  const canvasRef = useRef(null);

  useEffect(() => {
    if (!canvasRef.current) return;

    // Scene setup
    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(
      75,
      window.innerWidth / window.innerHeight,
      0.1,
      1000
    );
    const renderer = new THREE.WebGLRenderer({
      canvas: canvasRef.current,
      alpha: true,
      antialias: true,
    });

    renderer.setSize(window.innerWidth, window.innerHeight);
    renderer.setPixelRatio(window.devicePixelRatio);
    camera.position.z = 30;

    // Quantum Network Nodes
    const nodes = [];
    const nodeConnections = [];
    const nodeCount = 12; // Reduced from 15

    // Create quantum nodes
    const nodeGeometry = new THREE.IcosahedronGeometry(0.4, 1); // Slightly smaller
    const nodeMaterial = new THREE.MeshPhongMaterial({
      color: 0x00ffff,
      emissive: 0x0066ff,
      emissiveIntensity: 0.3, // Reduced intensity
      wireframe: true,
      transparent: true,
      opacity: 0.4, // Reduced opacity
    });

    // Add lighting for depth
    const ambientLight = new THREE.AmbientLight(0x404040, 0.5);
    scene.add(ambientLight);

    const pointLight = new THREE.PointLight(0x00ffff, 0.8, 100); // Reduced intensity
    pointLight.position.set(0, 0, 20);
    scene.add(pointLight);

    // Create node network
    for (let i = 0; i < nodeCount; i++) {
      const node = new THREE.Mesh(nodeGeometry, nodeMaterial.clone());

      // Position nodes in 3D space
      const theta = (i / nodeCount) * Math.PI * 2;
      const phi = Math.acos(2 * Math.random() - 1);
      const radius = 15 + Math.random() * 10;

      node.position.x = radius * Math.sin(phi) * Math.cos(theta);
      node.position.y = radius * Math.sin(phi) * Math.sin(theta);
      node.position.z = radius * Math.cos(phi);

      node.userData = {
        originalPosition: node.position.clone(),
        offset: Math.random() * Math.PI * 2,
        rotationSpeed: {
          x: (Math.random() - 0.5) * 0.01, // Slower rotation
          y: (Math.random() - 0.5) * 0.01,
          z: (Math.random() - 0.5) * 0.01,
        },
      };

      scene.add(node);
      nodes.push(node);
    }

    // Create energy connections between nodes
    const connectionMaterial = new THREE.LineBasicMaterial({
      color: 0x0099ff,
      transparent: true,
      opacity: 0.1, // Very subtle connections
    });

    // Connect nearby nodes
    for (let i = 0; i < nodes.length; i++) {
      for (let j = i + 1; j < nodes.length; j++) {
        const distance = nodes[i].position.distanceTo(nodes[j].position);

        if (distance < 12) {
          const points = [];
          points.push(nodes[i].position);
          points.push(nodes[j].position);

          const geometry = new THREE.BufferGeometry().setFromPoints(points);
          const connection = new THREE.Line(geometry, connectionMaterial.clone());
          connection.userData = {
            node1: nodes[i],
            node2: nodes[j],
            pulseOffset: Math.random() * Math.PI * 2,
          };

          scene.add(connection);
          nodeConnections.push(connection);
        }
      }
    }

    // Add quantum particles
    const particleCount = 60; // Reduced from 100
    const particlesGeometry = new THREE.BufferGeometry();
    const particlesPositions = [];
    const particlesVelocities = [];

    for (let i = 0; i < particleCount; i++) {
      particlesPositions.push((Math.random() - 0.5) * 50);
      particlesPositions.push((Math.random() - 0.5) * 50);
      particlesPositions.push((Math.random() - 0.5) * 50);

      particlesVelocities.push((Math.random() - 0.5) * 0.05); // Slower particles
      particlesVelocities.push((Math.random() - 0.5) * 0.05);
      particlesVelocities.push((Math.random() - 0.5) * 0.05);
    }

    particlesGeometry.setAttribute(
      'position',
      new THREE.Float32BufferAttribute(particlesPositions, 3)
    );

    const particlesMaterial = new THREE.PointsMaterial({
      color: 0x00ffff,
      size: 0.3, // Smaller particles
      transparent: true,
      opacity: 0.4, // More transparent
      blending: THREE.AdditiveBlending,
    });

    const particleSystem = new THREE.Points(particlesGeometry, particlesMaterial);
    scene.add(particleSystem);

    // Add central quantum core
    const coreGeometry = new THREE.SphereGeometry(2, 32, 32);
    const coreMaterial = new THREE.MeshPhongMaterial({
      color: 0x0066ff,
      emissive: 0x00ffff,
      emissiveIntensity: 0.1, // Very subtle core
      transparent: true,
      opacity: 0.15,
      wireframe: true,
    });
    const core = new THREE.Mesh(coreGeometry, coreMaterial);
    scene.add(core);

    // Add outer energy rings
    const rings = [];
    for (let i = 0; i < 2; i++) { // Reduced to 2 rings
      const ringGeometry = new THREE.TorusGeometry(8 + i * 4, 0.05, 8, 100); // Thinner rings
      const ringMaterial = new THREE.MeshBasicMaterial({
        color: 0x00ffff,
        transparent: true,
        opacity: 0.1 - i * 0.03, // Very subtle rings
      });
      const ring = new THREE.Mesh(ringGeometry, ringMaterial);
      ring.rotation.x = Math.random() * Math.PI;
      ring.rotation.y = Math.random() * Math.PI;
      ring.userData = {
        rotationSpeed: {
          x: (Math.random() - 0.5) * 0.005, // Very slow rotation
          y: (Math.random() - 0.5) * 0.005,
          z: (Math.random() - 0.5) * 0.005,
        },
      };
      scene.add(ring);
      rings.push(ring);
    }

    // Animation variables
    let time = 0;
    let animationFrameId;

    // Animation function
    function animateQuantum() {
      animationFrameId = requestAnimationFrame(animateQuantum);
      time += 0.005; // Slower overall time

      // Animate quantum nodes
      nodes.forEach((node) => {
        const offset = node.userData.offset;
        node.position.x = node.userData.originalPosition.x + Math.sin(time + offset) * 0.5;
        node.position.y = node.userData.originalPosition.y + Math.cos(time + offset) * 0.5;
        node.position.z =
          node.userData.originalPosition.z + Math.sin(time * 0.5 + offset) * 0.5;

        node.rotation.x += node.userData.rotationSpeed.x;
        node.rotation.y += node.userData.rotationSpeed.y;
        node.rotation.z += node.userData.rotationSpeed.z;

        const scale = 1 + Math.sin(time * 2 + offset) * 0.05;
        node.scale.set(scale, scale, scale);

        node.material.opacity = 0.3 + Math.sin(time * 2 + offset) * 0.2;
      });

      // Update connections
      nodeConnections.forEach((connection) => {
        const node1 = connection.userData.node1;
        const node2 = connection.userData.node2;

        const positions = connection.geometry.attributes.position.array;
        positions[0] = node1.position.x;
        positions[1] = node1.position.y;
        positions[2] = node1.position.z;
        positions[3] = node2.position.x;
        positions[4] = node2.position.y;
        positions[5] = node2.position.z;
        connection.geometry.attributes.position.needsUpdate = true;

        connection.material.opacity =
          0.05 + Math.sin(time * 3 + connection.userData.pulseOffset) * 0.05;
      });

      // Animate particles
      const positions = particleSystem.geometry.attributes.position.array;
      for (let i = 0; i < particleCount * 3; i += 3) {
        positions[i] += particlesVelocities[i];
        positions[i + 1] += particlesVelocities[i + 1];
        positions[i + 2] += particlesVelocities[i + 2];

        if (Math.abs(positions[i]) > 25) particlesVelocities[i] *= -1;
        if (Math.abs(positions[i + 1]) > 25) particlesVelocities[i + 1] *= -1;
        if (Math.abs(positions[i + 2]) > 25) particlesVelocities[i + 2] *= -1;
      }
      particleSystem.geometry.attributes.position.needsUpdate = true;
      particleSystem.rotation.y += 0.0002;

      // Animate core
      core.rotation.x += 0.002;
      core.rotation.y += 0.001;
      const coreScale = 1 + Math.sin(time) * 0.1;
      core.scale.set(coreScale, coreScale, coreScale);
      core.material.emissiveIntensity = 0.1 + Math.sin(time * 2) * 0.05;

      // Animate rings
      rings.forEach((ring) => {
        ring.rotation.x += ring.userData.rotationSpeed.x;
        ring.rotation.y += ring.userData.rotationSpeed.y;
        ring.rotation.z += ring.userData.rotationSpeed.z;
      });

      // Rotate entire scene slowly
      scene.rotation.y += 0.0005;

      // Move light
      pointLight.position.x = Math.sin(time) * 20;
      pointLight.position.z = Math.cos(time) * 20;

      renderer.render(scene, camera);
    }

    animateQuantum();

    // Mouse interaction
    const handleMouseMove = (e) => {
      const mouseX = (e.clientX / window.innerWidth) * 2 - 1;
      const mouseY = -(e.clientY / window.innerHeight) * 2 + 1;

      camera.position.x = mouseX * 2; // Reduced movement range
      camera.position.y = mouseY * 2;
      camera.lookAt(0, 0, 0);
    };

    document.addEventListener('mousemove', handleMouseMove);

    // Window resize handler
    const handleResize = () => {
      camera.aspect = window.innerWidth / window.innerHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(window.innerWidth, window.innerHeight);
    };

    window.addEventListener('resize', handleResize);

    // Cleanup
    return () => {
      cancelAnimationFrame(animationFrameId);
      document.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('resize', handleResize);
      renderer.dispose();
    };
  }, []);

  return (
    <>
      <div className="fixed w-full h-full top-0 left-0 -z-30 bg-gradient-to-b from-[#0f172a] to-[#1e293b]"></div>
      <div className="fixed w-full h-full top-0 left-0 -z-20 bg-[radial-gradient(ellipse_at_top_left,_rgba(0,102,255,0.05)_0%,_transparent_40%),_radial-gradient(ellipse_at_bottom_right,_rgba(0,212,255,0.04)_0%,_transparent_40%)]"></div>
      <canvas
        ref={canvasRef}
        className="fixed top-0 left-0 w-full h-full -z-10 opacity-30" // Reduced canvas opacity
      />
    </>
  );
}

export default QuantumBackground;
